

// Replace with your actual socket URL

import React, { useEffect, useRef, useState, useContext } from "react";
import { Camera, Loader, UserCheck, X, Video, StopCircle } from 'lucide-react';

import { io } from "socket.io-client";
import NearbyDeviceList from "./NearbyDeviceList";

import useRequest from "../../hooks/useRequest"
import PlayersList from "./PlayersList";

// Replace with your actual socket URL
import { SOCKET_URL } from "../../constants/api_urls";
import { HoleContext } from "../../pages/HoleContext";
import { useLocationTracker } from "./useLocationTracker";



const LiveCam = ({ gdetail }) => {
    const socketRef = useRef(null);
    const [isConnected, setIsConnected] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState("Disconnected");
    const [connectedCamera, setConnectedCamera] = useState(null);
    const [nearbyDevices, setNearbyDevices] = useState([]);
    const [countdownData, setCountdownData] = useState(null);
    const [cameraRecords, setCameraRecords] = useState(null);
    const [recordedPlayerIds, setRecordedPlayerIds] = useState([]);
    const [aroundDevices, setAroundDevices] = useState([]);


    const [logs, setLogs] = useState([]);
    const { request } = useRequest();

    const [players, setPlayers] = useState([]);
    const { currentHole } = useContext(HoleContext);


    // Add a log entry
    const addLog = (message) => {
        const timestamp = new Date().toLocaleTimeString();
        setLogs(prevLogs => [{ timestamp, message }, ...prevLogs.slice(0, 19)]);
        // console.log("logs", logs)
    };

    const handleLocationUpdate = (locationData) => {
        // Optionally handle location in component
        // console.log("Location received:", locationData);
    };

    const {
        userLocation,
        locationStatus
    } = useLocationTracker({
        onLocationUpdate: handleLocationUpdate,
        addLog,
        connectedCamera,
        socketRef
    });


    // Initialize socket connection
    useEffect(() => {
        // Clean up function to track component mounting state
        let isComponentMounted = true;

        try {
            addLog("Initializing socket connection...");

            // Initialize socket with robust configuration
            const socket = io(SOCKET_URL, {
                transports: ["websocket", "polling"],
                reconnectionAttempts: Infinity,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 5000,
                timeout: 20000,
                forceNew: true,
                autoConnect: true
            });

            socketRef.current = socket;

            // Socket event handlers
            socket.on("connect", () => {
                if (isComponentMounted && gdetail?._id) {
                    setIsConnected(true);
                    setConnectionStatus("Connected");
                    addLog(`Connected to server. Socket ID: ${socket.id}`);

                    // Identify as a user
                    socket.emit("identify", {
                        type: "user",
                        userId: gdetail?.user._id,
                        gameId: gdetail?._id
                    });
                    addLog("Sent identify event");

                    // Send location if available
                    if (userLocation) {
                        socket.emit("user-location", userLocation);
                        addLog("Sent initial location data");
                    }

                    // Check proximity
                    setTimeout(() => {
                        socket.emit("check-proximity");
                        addLog("Sent check-proximity event");

                        // Also check for around devices
                        socket.emit("check-around-devices");
                        addLog("Sent check-around-devices event");
                    }, 1000);
                }
            });

            socket.io.on("reconnect_attempt", (attempt) => {
                if (isComponentMounted) {
                    setConnectionStatus(`Reconnecting... (Attempt ${attempt})`);
                    addLog(`Reconnection attempt #${attempt}`);
                }
            });

            socket.io.on("reconnect", () => {
                if (isComponentMounted) {
                    setConnectionStatus("Reconnected");
                    addLog("Reconnected to server");

                    // Re-identify after reconnection
                    socket.emit("identify", {
                        type: "user",
                        userId: gdetail?.user._id,
                        gameId: gdetail?._id
                    });
                    addLog("Re-sent identify event after reconnection");

                    // Re-send location after reconnection
                    if (userLocation) {
                        socket.emit("user-location", userLocation);
                        addLog("Re-sent location after reconnection");
                    }
                }
            });

            socket.on("connect_error", (error) => {
                if (isComponentMounted) {
                    setConnectionStatus(`Error: ${error.message}`);
                    addLog(`Connection error: ${error.message}`);
                }
            });

            socket.on("disconnect", (reason) => {
                if (isComponentMounted) {
                    setIsConnected(false);
                    setConnectionStatus(`Disconnected: ${reason}`);
                    setConnectedCamera(null);
                    addLog(`Disconnected: ${reason}`);

                    if (reason === "io server disconnect") {
                        // Server forced disconnect - manual reconnection needed
                        addLog("Server forced disconnect - attempting reconnection");
                        socket.connect();
                    }
                }
            });

            // Listen for nearby devices
            socket.on("nearby-devices", (devices) => {
                if (isComponentMounted) {
                    setNearbyDevices(devices);
                    addLog(`Received ${devices.length} nearby devices`);
                    // console.log(`Received ${devices.length} nearby devices`)
                }
            });
            socket.on("around-devices", (devices) => {
                if (isComponentMounted) {
                    setAroundDevices(devices);
                    addLog(`Received ${devices.length} devices around you`);
                    // console.log(`Received ${devices.length} devices around you`);
                }
            });

            // Camera connection events
            socket.on("camera-connected", (data) => {
                if (isComponentMounted) {
                    setConnectedCamera(data.device_id);
                    addLog(`Connected to camera: ${data.device_id}`);
                }
            });

            socket.on("camera-disconnected", (data) => {
                if (isComponentMounted) {
                    setConnectedCamera(null);
                    addLog(`Disconnected from camera: ${data.device_id}. Reason: ${data.reason || "user_action"}`);
                }
            });

            socket.on("camera-connection-error", (data) => {
                if (isComponentMounted) {
                    addLog(`Camera connection error: ${data.message}`);
                }
            });

            // Recording updates
            socket.on("recored-update-num", (data) => {
                if (isComponentMounted) {
                    // setCountdownData(data);
                    addLog(`Recording update: Countdown ${data.countdown}, User ${data.userid}, Done: ${data.isDone}`);
                    // Call the PlayersList's exposed method to update countdown
                    if (window.handlePlayerCountdownUpdate) {
                        window.handlePlayerCountdownUpdate(data);
                    }

                    if (data?.isDone) {
                        RecoredUser(data?.userid, gdetail?._id);
                    }
                }
            });

            // Debug messages
            socket.on("debug", (message) => {
                if (isComponentMounted) {
                    addLog(`Debug: ${message}`);
                }
            });

            // Heartbeat response
            socket.on("heartbeat-response", () => {
                if (isComponentMounted) {
                    addLog("Received heartbeat response");
                }
            });

            // Setup heartbeat
            const heartbeatInterval = setInterval(() => {
                if (socket.connected) {
                    socket.emit("heartbeat");
                    addLog("Sent heartbeat");
                }
            }, 20000);

            // Cleanup function
            return () => {
                isComponentMounted = false;
                clearInterval(heartbeatInterval);
                socket.disconnect();
                addLog("Component unmounted - socket disconnected");
            };

        } catch (err) {
            if (isComponentMounted) {
                setConnectionStatus(`Error creating socket: ${err.message}`);
                addLog(`Socket creation error: ${err.message}`);
            }
        }
    }, []);


    // When location changes, send it to the server if connected
    useEffect(() => {
        if (userLocation && socketRef.current?.connected && !connectedCamera) {
            // Use a debounce mechanism to avoid sending too many updates
            const timer = setTimeout(() => {
                socketRef.current.emit("user-location", userLocation);
                addLog("Sent updated location to server");
            }, 5000); // Send updates at most every 5 seconds

            return () => clearTimeout(timer);
        }
    }, [userLocation, isConnected, connectedCamera]);


    // Camera control functions
    const connectToCamera = (deviceId) => {
        if (socketRef.current?.connected) {
            addLog(`Attempting to connect to camera: ${deviceId}`);
            socketRef.current.emit("connect-to-camera", deviceId);
        } else {
            addLog("Cannot connect to camera: Socket not connected");
        }
    };

    const disconnectFromCamera = () => {
        if (socketRef.current?.connected && connectedCamera) {
            addLog(`Disconnecting from camera: ${connectedCamera}`);
            socketRef.current.emit("disconnect-from-camera", connectedCamera);
        } else {
            addLog("Cannot disconnect: No camera connected");
        }
    };


    // Sort players to put creator at the top
    const sortedPlayers = [...players].sort((a, b) => {
        if (a.isCreator) return -1;
        if (b.isCreator) return 1;
        return 0;
    });

    useEffect(() => {
        // Extract players from gdetail.gameDetailsId
        if (gdetail && gdetail.gameDetailsId && Array.isArray(gdetail.gameDetailsId)) {
            const extractedPlayers = gdetail.gameDetailsId.map(detail => ({
                userId: detail.user._id,
                name: detail.user.name || "Player",
                profilePic: detail.user.profilePic || "",
                isCreator: detail.user._id === (gdetail.user?._id || "")
            }));

            setPlayers(extractedPlayers);
            // console.log("Extracted players:", extractedPlayers);
        }
    }, [gdetail]);



    // Add this useEffect to check if all players have recorded and disconnect
    useEffect(() => {
        // Only run this check if we have:
        // 1. A connected camera
        // 2. Players list loaded
        // 3. Recorded player IDs loaded
        if (connectedCamera && players.length > 0 && recordedPlayerIds.length > 0) {
            // Check if all players have recorded
            const allPlayersRecorded = players.every(player =>
                recordedPlayerIds.includes(player.userId)
            );

            // If all players have recorded, disconnect from the camera
            if (allPlayersRecorded) {
                addLog("All players have recorded videos - automatically disconnecting from camera");
                disconnectFromCamera();
            }
        }
    }, [connectedCamera, players, recordedPlayerIds]);


    // Function to start recording
    const startRec = (playerId) => {
        if (isConnected && connectedCamera) {
            // console.log("Player ID:", playerId, "connected camera", connectedCamera);
            const data = {
                course: gdetail?.courseData.name,
                holes: gdetail.holes,
                isPrivate: gdetail.posts_privacy,
                token: localStorage.getItem("token"),
            }
            // console.log("data", data)
            socketRef.current.emit("start-rec", connectedCamera, playerId, gdetail?._id, gdetail?.user?._id, data, currentHole);
            // on backend   socket.on("start-rec", (deviceId: string, userid: string, gameid: string, creator:string) => {
        } else {
            console.warn("Cannot start recording: Not connected or no camera connected");
        }
    };

    // Function to stop recording
    const stopRec = (playerId) => {
        if (isConnected && connectedCamera) {
            // console.log("Player ID:", playerId, "connected camera", connectedCamera);
            socketRef.current.emit("stop-rec", connectedCamera, playerId, gdetail?._id, gdetail?.user?._id, currentHole);
        } else {
            console.warn("Cannot stop recording: Not connected or no camera connected");
        }
    };

    useEffect(() => {
        if (connectedCamera) {
            disconnectFromCamera()
        }
        // console.log("current hole", currentHole)
        if (gdetail?._id) {
            getCameraRecords(gdetail._id, currentHole);
        }
    }, [currentHole, gdetail?._id]);

    const getCameraRecords = (gameId, hole) => {
        // Build query parameters
        let url = `/app/camera/get-game-camera-record?gameId=${gameId}`;
        if (hole) {
            url += `&hole=${hole}`;
        }

        request({
            url: url,
            method: "GET",
            onSuccess: (data) => {
                // console.log("Camera records retrieved:", data);

                // Extract recorded player IDs and store them
                const recordedIds = [];
                if (data?.data?.data?.[0]?.players) {
                    data.data.data[0].players.forEach(player => {
                        recordedIds.push(player._id);
                    });
                }

                // console.log("Recorded player IDs:", recordedIds);
                setRecordedPlayerIds(recordedIds);

                // Store the records for use in the PlayersList
                setCameraRecords(data);
            },
            onError: (error) => {
                // console.log("Error retrieving camera records:", error);
            },
        });
    }


    // Debug info
    useEffect(() => {
        if (players.length > 0 && recordedPlayerIds.length > 0) {
            // console.log("Current players:", players.map(p => ({ name: p.name, userId: p.userId })));
            // console.log("Recorded player IDs:", recordedPlayerIds);

            const recordedPlayerNames = players
                .filter(p => recordedPlayerIds.includes(p.userId))
                .map(p => p.name);

            // console.log("Players who have recorded:", recordedPlayerNames);
        }
    }, [players, recordedPlayerIds]);


    const RecoredUser = (userId, gameId) => {
        // console.log("RecoredUser", userId, gameId, "currenthole", currentHole)
        var post = {
            gameId,
            hole: currentHole,
            userId,
        };

        request({
            url: "/app/camera/add-user-camera-recored",
            method: "POST",
            data: post,
            onSuccess: (data) => {
                // console.log("Recording added:", data)
                getCameraRecords(gameId, currentHole);
            },
            onError: (error) => {
                // console.log("Recording error:", error)
            },
        });
    }



    //    html code part here 
    return (


        <div className="max-w-3xl w-full mx-auto p-4 font-sans">
            {aroundDevices.length > 0 && nearbyDevices.length === 0 && (
                <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-md">
                    <div className="flex items-center">
                        <Camera className="mr-3 text-blue-500" size={24} />
                        <h3 className="text-lg font-medium text-blue-700" style={{
                            color: "#dee2e6"
                        }}>
                            Found Cameras Around You (10-20m)
                        </h3>
                    </div>
                    <p className="mt-2 text-blue-600" style={{
                        color: "#dee2e6"
                    }}>Move closer to use them</p>
                </div>
            )}




            {
                nearbyDevices.length > 0 && (
                    <>
                        <div style={{
                            padding: "15px",
                            marginBottom: "20px",
                            borderRadius: "8px",
                            backgroundColor: "rgb(10 86 80 / 39%)",
                        }}>

                            <div>
                                {
                                    nearbyDevices.length > 0 && (
                                        <><h3 style={{
                                            color: "#dee2e6"
                                        }}>Nearby Camera 📸 (Within 10m)</h3>

                                            <NearbyDeviceList
                                                devices={nearbyDevices}
                                                onConnect={connectToCamera}
                                                connectedCamera={connectedCamera}
                                                onDisconnect={disconnectFromCamera} /></>
                                    )
                                }

                            </div>
                            {connectedCamera && (
                                <PlayersList
                                    players={sortedPlayers}
                                    onStartRec={startRec}
                                    onStopRec={stopRec}
                                    cameraRecords={cameraRecords}
                                />
                            )}

                        </div>
                    </>
                )
            }
        </div>
    );
};

export default LiveCam;